import { useState } from 'react';
import { useHistory } from 'react-router';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { toApiBoolean } from '@liquidweb-dev/club-sauce/types/boolean';
import Card from '@liquidweb-dev/undercurrent/Card';
import CardHeader from '@liquidweb-dev/undercurrent/CardHeader';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import CardLayeredFooter from '@liquidweb-dev/undercurrent/CardLayeredFooter';
import LoaderCircleIcon from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import TextField from '@liquidweb-dev/undercurrent/TextField';
import toast from '@liquidweb-dev/undercurrent/toast';
import useAssetDetails from 'modules/queries/asset/useDetails';
import useAssetRemove from 'modules/queries/asset/useRemove';
import hostingDetailsAlsowith from 'containers/pages/servers/details/hostingDetailsAlsowith';
import LWForm, { type FormSubmitHandler } from 'components/LWForm';
import HookedSubmit from 'components/standard/Form/HookedSubmit';
import CancellationLoader from './CancellationLoader';
import AssetOverview from './AssetOverview';
import { CancellationFormData } from './constants';

dayjs.extend(localizedFormat);

export interface FastCancellationProps {
	uniqId: string;
	formValues: CancellationFormData;
	onFormValuesChange: (values: CancellationFormData) => void;
	onSubmit: FormSubmitHandler<CancellationFormData>;
}

const FastCancellation = ({
	uniqId,
	formValues,
	onFormValuesChange,
	onSubmit,
}: FastCancellationProps) => {
	const history = useHistory();
	const { isLoading: isAssetDetailsLoading, data: assetDetails } =
		useAssetDetails(
			{ uniq_id: uniqId, alsowith: hostingDetailsAlsowith },
			{ enabled: Boolean(uniqId) },
		);
	const { isLoading, mutate: removeAsset } = useAssetRemove();
	const [confirmationText, setConfirmationText] = useState('');

	const handleSubmit = () => {
		if (!assetDetails?.active) {
			toast.danger('Cannot remove an asset that is not "Active"');
			return;
		}

		removeAsset(
			{
				uniq_id: uniqId,
				cancellation_reason: formValues.cancellation_reason,
				cancellation_comment:
					formValues.additional_info.length > 0
						? formValues.additional_info
						: undefined,
				cleanup_assets: toApiBoolean(formValues.cleanup_assets),
			},
			{
				onSuccess: () => {
					onSubmit(formValues);
					history.replace('/servers');
				},
			},
		);
	};

	return (
		<LWForm
			values={formValues}
			onValuesChange={onFormValuesChange}
			onSubmit={handleSubmit}
		>
			<Stack width="100%" direction="column" gap={500} useFlexGap>
				<Stack direction="column" gap={100} useFlexGap>
					<Typography fontWeight="500" variant="title-md">
						Confirm your cancellation
					</Typography>
					<Typography variant="body-md">
						By clicking Confirm, you agree that the service associated with the
						IP below will be cancelled on the date listed. Any billing for this
						service will be stopped and any other services associated with this
						account will NOT be affected. The cancellation will become effective
						on the date listed.
					</Typography>
				</Stack>
				<Stack>
					<Card sx={{ zIndex: 1 }}>
						<CardHeader
							title={
								<Stack
									direction={{ xs: 'column', sm: 'row' }}
									justifyContent="space-between"
									alignItems={{ xs: 'flex-start', sm: 'center' }}
									gap={{ xs: 100 }}
									width="100%"
								>
									<Typography variant="label-lg" fontWeight={500}>
										Selected Service
									</Typography>
									<Typography
										variant="label-lg"
										sx={({ vars }) => ({
											color: vars.palette.uc.text.weak,
										})}
									>
										Cancellation date:{' '}
										<Typography
											variant="label-lg"
											sx={({ vars }) => ({
												color: vars.palette.uc.text.main,
											})}
										>
											{dayjs().format('LL')}
										</Typography>
									</Typography>
								</Stack>
							}
						/>
						<CardContent
							sx={{ display: 'flex', flexDirection: 'column', gap: 200 }}
						>
							{isAssetDetailsLoading ? (
								<CancellationLoader
									title="Fetching your details..."
									icon={<LoaderCircleIcon />}
								/>
							) : (
								<AssetOverview data={assetDetails} />
							)}
						</CardContent>
					</Card>
					<CardLayeredFooter
						sx={(theme) => ({
							backgroundColor: theme.vars.palette.uc.danger.bg.main,
							display: 'flex',
							flexDirection: 'column',
							gap: 200,
							position: 'relative',
							paddingTop: 300,
							marginTop: -150,
							paddingBottom: 200,
							zIndex: 0,
						})}
					>
						<Typography variant="label-lg" fontWeight={500}>
							To verify, type{' '}
							<Typography variant="label-lg" fontWeight={700}>
								CONTINUE
							</Typography>{' '}
							below
						</Typography>
						<TextField
							placeholder="CONTINUE"
							onChange={(event) => setConfirmationText(event.target.value)}
							autoComplete="off"
							fullWidth
							sx={(theme) => ({
								backgroundColor: theme.vars.palette.uc.bg.main,
								borderRadius: theme.borderRadius.roundedSm,
								border: `1px solid ${theme.vars.palette.uc.border.main}`,
							})}
						/>
					</CardLayeredFooter>
				</Stack>
				<Stack alignItems="center">
					<HookedSubmit
						size="large"
						variant="secondary"
						color="danger"
						loading={isLoading}
						disabled={confirmationText !== 'CONTINUE' || isLoading}
						sx={(theme) => ({
							minWidth: 248,
							padding: `${theme.spacing(100)} ${theme.spacing(200)}`,
						})}
					>
						<Typography
							variant="label-lg"
							fontWeight={500}
							sx={(theme) => ({
								color: theme.vars.palette.uc.danger.text.strong,
							})}
						>
							Confirm cancellation
						</Typography>
					</HookedSubmit>
				</Stack>
			</Stack>
		</LWForm>
	);
};

export default FastCancellation;
